import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAsync, useSession, useToast } from '../../lib/store';
import type {
  WorldBossAdminRow,
  WorldBossCatalog,
  WorldBossLive,
  WorldBossPreview,
  WorldBossSimulation,
  WorldBossSupply,
} from '../../lib/types';
import { Button, EmptyState, ErrorState, Panel, Tabs } from '../ui';
import { formatDate, formatNumber } from '../../lib/format';
import { WorldBossForm } from './WorldBossForm';

/**
 * Admin → Events → World Boss — design PART 40, 42, 43, 44, 45, 46, 48.
 *
 * Four things this screen exists to do, in the order they matter:
 *
 *   1. **Say how many Legendaries an event will create, before it runs.** That
 *      is PART 42's headline and it is the largest number on the page. Nobody
 *      should have to derive it from a reward zone.
 *   2. **Recommend an HP target from the real player population** (PART 43), so
 *      nobody has to guess and produce the fifty-billion-HP boss the design
 *      names as the failure mode.
 *   3. **Show a live fight** — pool, rate, distribution, and who the integrity
 *      scan flagged (PART 44).
 *   4. **Give a way to correct a genuine mistake without being a way to mint
 *      Legendaries** (PART 46). Every control here either changes what a fight
 *      *will* do, or grants a reward somebody was already owed through the
 *      idempotent path — there is no button that edits damage or reorders a
 *      placing, because PART 45 says there must not be.
 *
 * Creating and editing a boss is deliberately **not** a second event wizard.
 * A World Boss *is* an event: its dates, copy, theme and reward definitions are
 * authored on the Events tab like everything else, and this screen configures
 * the fight that hangs off one. Duplicating the event form here would be two
 * forms that can disagree about the same row.
 */

type Tab = 'list' | 'live' | 'supply';

export function WorldBossAdmin() {
  const { me } = useSession();
  const [tab, setTab] = useState<Tab>('list');
  const [selected, setSelected] = useState<string | null>(null);

  const can = (permission: string) => Boolean(me?.permissions?.includes(permission));

  return (
    <div className="stack">
      <Tabs
        tabs={[
          { value: 'list', label: 'Fights' },
          { value: 'live', label: 'Live view' },
          { value: 'supply', label: 'Legendary supply' },
        ]}
        value={tab}
        onChange={(next) => setTab(next as Tab)}
      />

      {tab === 'list' && <BossList selected={selected} onSelect={setSelected} can={can} />}
      {tab === 'live' && <LiveView bossId={selected} onSelect={setSelected} can={can} />}
      {tab === 'supply' && <Supply />}
    </div>
  );
}

/* -------------------------------------------------------------------------
   The list, the preview and the simulation
   ------------------------------------------------------------------------- */

function BossList({
  selected,
  onSelect,
  can,
}: {
  selected: string | null;
  onSelect: (id: string | null) => void;
  can: (permission: string) => boolean;
}) {
  const state = useAsync<{ bosses: WorldBossAdminRow[] }>(() => api.get('/admin/world-boss'));
  // Fetched here rather than inside the form so opening it is instant, and so
  // the New button can be disabled when there is no event to attach a fight to.
  const catalog = useAsync<WorldBossCatalog>(() => api.get('/admin/world-boss/catalog'));
  const [creating, setCreating] = useState(false);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (state.loading && !state.data) return <div className="pxwell skeleton" style={{ height: 200 }} />;

  const bosses = state.data?.bosses ?? [];

  return (
    <div className="stack">
      {creating && catalog.data && (
        <WorldBossForm
          catalog={catalog.data}
          editing={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            state.reload();
            catalog.reload();
          }}
        />
      )}

      <Panel
        title="World Boss fights"
        hint={`${bosses.length} on record`}
        action={
          can('events.manage') ? (
            <Button size="sm" onClick={() => setCreating(true)} disabled={!catalog.data}>
              New fight
            </Button>
          ) : undefined
        }
      >
        {bosses.length === 0 ? (
          <EmptyState art="⚡" title="No World Boss configured">
            Create one with <strong>New fight</strong>, or run{' '}
            <code>npm run seed:worldboss -w server</code> for the two starter templates — a
            non-Legendary preseason boss and Stormfront.
          </EmptyState>
        ) : (
          <div className="stack-sm">
            {bosses.map((boss) => (
              <div key={boss.id} className="wb-admin__row">
                <img className="pixel" src={boss.sprite ?? ''} alt="" />
                <div className="wb-admin__row-body">
                  <strong>
                    {boss.bossName}
                    {boss.title && ` — ${boss.title}`}
                  </strong>
                  <span className="tiny muted">
                    {boss.event.name} · {boss.status} · {boss.event.status} ·{' '}
                    {formatDate(boss.event.startsAt)} → {formatDate(boss.event.endsAt)}
                  </span>
                  <span className="tiny muted">
                    {formatNumber(boss.hp.remaining)} / {formatNumber(boss.hp.max)} HP ·{' '}
                    {boss.participants} participants · ranks #{boss.zone.from}–#{boss.zone.to}
                  </span>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  <span className="chip">{boss.maxLegendaries} max</span>
                  <Button size="sm" variant={selected === boss.id ? 'primary' : 'ghost'} onClick={() => onSelect(boss.id)}>
                    {selected === boss.id ? 'Selected' : 'Open'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {selected && <BossDetail bossId={selected} can={can} onChanged={state.reload} />}
    </div>
  );
}

function BossDetail({
  bossId,
  can,
  onChanged,
}: {
  bossId: string;
  can: (permission: string) => boolean;
  onChanged: () => void;
}) {
  const { push } = useToast();
  const state = useAsync<{ preview: WorldBossPreview }>(
    () => api.get(`/admin/world-boss/${bossId}/preview`),
    [bossId],
  );
  const [sim, setSim] = useState<WorldBossSimulation | null>(null);
  const [players, setPlayers] = useState('');
  const [busy, setBusy] = useState(false);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 240 }} />;

  const preview = state.data.preview;

  async function simulate() {
    setBusy(true);
    try {
      const result = await api.post<{ simulation: WorldBossSimulation }>(
        `/admin/world-boss/${bossId}/simulate`,
        players ? { expectedParticipants: Number(players) } : {},
      );
      setSim(result.simulation);
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not simulate.');
    } finally {
      setBusy(false);
    }
  }

  async function setHp(value: number) {
    const reason = window.prompt(
      'Why is this HP target changing? This is recorded on the event and shown to players.',
    );
    if (!reason || reason.trim().length < 5) return;
    try {
      await api.post(`/admin/world-boss/${bossId}/hp`, { maxHp: value, reason: reason.trim() });
      push('success', 'HP target updated.');
      state.reload();
      onChanged();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not change the HP.');
    }
  }

  return (
    <div className="stack">
      {/* PART 42's headline, and the first thing on the panel rather than the
          last. This is the number an operator is accountable for. */}
      <Panel title="Reward impact">
        <div className="wb-admin__impact">
          <span className="tiny">Maximum new Legendaries this fight can create</span>
          <span className="wb-admin__impact-figure">{preview.rewards.maxLegendariesCreated}</span>
          <span className="tiny">
            {preview.rewards.rewardLabel
              ? `${preview.rewards.rewardLabel} to ranks #${preview.rewards.zone.from}–#${preview.rewards.zone.to}`
              : 'No reward configured — this fight pays nothing but milestones.'}
          </span>
          <span className="tiny">
            {preview.rewards.grantOnEscape
              ? 'Paid even if the boss escapes.'
              : 'Paid only if the server defeats it.'}
          </span>
        </div>

        {preview.warnings.map((warning) => (
          <p key={warning} className="wb-admin__warning">
            {warning}
          </p>
        ))}
      </Panel>

      <Panel title={`${preview.boss.name} — configuration`} hint={preview.status}>
        <dl className="wb-admin__stats">
          <div>
            <dt>Level</dt>
            <dd>{preview.boss.level}</dd>
          </div>
          <div>
            <dt>Pool</dt>
            <dd className="mono">{formatNumber(preview.hp.max)}</dd>
          </div>
          <div>
            <dt>Encounter</dt>
            <dd>{preview.boss.encounterTurns} turns</dd>
          </div>
          <div>
            <dt>Attempts</dt>
            <dd>
              {preview.attempts.perDay}/day · {preview.attempts.overEvent} total
            </dd>
          </div>
          <div>
            <dt>Fighter lock</dt>
            <dd>{preview.fighterLock.mode}</dd>
          </div>
          <div>
            <dt>Duration</dt>
            <dd>{preview.event.durationHours}h</dd>
          </div>
          <div>
            <dt>Phase</dt>
            <dd>{preview.boss.phase ?? 'none'}</dd>
          </div>
          <div>
            <dt>Visible</dt>
            <dd>{preview.event.visible ? 'yes' : 'hidden'}</dd>
          </div>
        </dl>

        <p className="tiny muted">
          Dates, copy, theme and reward definitions belong to the event —{' '}
          <Link to="/admin/events">edit {preview.event.name}</Link>. A World Boss is an event, so it
          has no second set of any of those.
        </p>

        <p className="tiny muted">
          Stats: {Object.entries(preview.boss.stats).map(([key, value]) => `${key} ${value}`).join(' · ')}
        </p>
        <p className="tiny muted">Moves: {preview.boss.moves.join(', ')}</p>
      </Panel>

      {/* PART 43. The recommendation runs the real encounter code against the
          real player population, so it moves when the game's balance moves. */}
      <Panel
        title="Simulation"
        hint="Run this before setting a real HP target"
        action={
          can('events.manage') ? (
            <Button size="sm" onClick={simulate} disabled={busy}>
              {busy ? 'Running…' : 'Simulate'}
            </Button>
          ) : undefined
        }
      >
        <div className="stack-sm">
          <label className="tiny">
            Expected participants (blank = use the sample size)
            <input
              className="input"
              value={players}
              onChange={(event) => setPlayers(event.target.value.replace(/\D/g, ''))}
              placeholder="e.g. 300"
            />
          </label>

          {!sim ? (
            <p className="tiny muted">
              Samples the most active trainers, picks the Pokémon each would plausibly send, and runs
              the real encounter against this boss. Nothing is written.
            </p>
          ) : (
            <>
              <dl className="wb-admin__stats">
                <div>
                  <dt>Sampled</dt>
                  <dd>{sim.sampled} trainers</dd>
                </div>
                <div>
                  <dt>Median per attempt</dt>
                  <dd className="mono">{formatNumber(sim.perAttempt.median)}</dd>
                </div>
                <div>
                  <dt>Casual / event</dt>
                  <dd className="mono">{formatNumber(sim.perTrainer.casual)}</dd>
                </div>
                <div>
                  <dt>Hardcore / event</dt>
                  <dd className="mono">{formatNumber(sim.perTrainer.hardcore)}</dd>
                </div>
                <div>
                  <dt>Expected total</dt>
                  <dd className="mono">{formatNumber(sim.expectedTotal)}</dd>
                </div>
                <div>
                  <dt>Likely #1</dt>
                  <dd className="mono">{formatNumber(sim.rankThresholds.first)}</dd>
                </div>
                <div>
                  <dt>Likely #5</dt>
                  <dd className="mono">{formatNumber(sim.rankThresholds.fifth)}</dd>
                </div>
                <div>
                  <dt>Confidence it falls</dt>
                  <dd>{Math.round(sim.defeatOdds * 100)}%</dd>
                </div>
              </dl>

              <div className="wb-admin__impact">
                <span className="tiny">Recommended HP</span>
                <span className="wb-admin__impact-figure">{formatNumber(sim.recommendedHp)}</span>
                <span className="tiny">
                  Currently {formatNumber(sim.currentHp)} —{' '}
                  {Math.round((sim.currentHp / Math.max(1, sim.recommendedHp)) * 100)}% of the
                  recommendation.
                </span>
                {can('events.publish') && sim.currentHp !== sim.recommendedHp && (
                  <Button size="sm" onClick={() => setHp(sim.recommendedHp)}>
                    Set the pool to {formatNumber(sim.recommendedHp)}
                  </Button>
                )}
              </div>

              {sim.notes.map((note) => (
                <p key={note} className="tiny muted">
                  {note}
                </p>
              ))}
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}

/* -------------------------------------------------------------------------
   The live view and its controls — design PART 44 and 45
   ------------------------------------------------------------------------- */

function LiveView({
  bossId,
  onSelect,
  can,
}: {
  bossId: string | null;
  onSelect: (id: string | null) => void;
  can: (permission: string) => boolean;
}) {
  const { push } = useToast();
  const list = useAsync<{ bosses: WorldBossAdminRow[] }>(() => api.get('/admin/world-boss'));
  const live = useAsync<WorldBossLive>(
    () => (bossId ? api.get(`/admin/world-boss/${bossId}/live`) : Promise.resolve(null as never)),
    [bossId],
  );

  if (!bossId) {
    return (
      <Panel title="Live view">
        <EmptyState art="◎" title="Pick a fight">
          Choose one on the Fights tab.
        </EmptyState>
        {(list.data?.bosses ?? []).map((boss) => (
          <Button key={boss.id} size="sm" variant="ghost" onClick={() => onSelect(boss.id)}>
            {boss.bossName} — {boss.status}
          </Button>
        ))}
      </Panel>
    );
  }

  if (live.error) return <ErrorState message={live.error} onRetry={live.reload} />;
  if (!live.data) return <div className="pxwell skeleton" style={{ height: 260 }} />;

  const data = live.data;
  const maxBucket = Math.max(1, ...data.histogram.map((bucket) => bucket.count));

  async function act(path: string, body: unknown, message: string) {
    try {
      await api.post(`/admin/world-boss/${bossId}${path}`, body);
      push('success', message);
      live.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'That did not work.');
    }
  }

  return (
    <div className="stack">
      <Panel title={data.boss.name} hint={data.boss.status}>
        <dl className="wb-admin__stats">
          <div>
            <dt>Remaining</dt>
            <dd className="mono">{formatNumber(data.boss.hp.remaining)}</dd>
          </div>
          <div>
            <dt>Pool</dt>
            <dd className="mono">{formatNumber(data.boss.hp.max)}</dd>
          </div>
          <div>
            <dt>Percent</dt>
            <dd>{data.boss.hp.percent.toFixed(1)}%</dd>
          </div>
          <div>
            <dt>Damage / hour</dt>
            <dd className="mono">{formatNumber(data.rate.damagePerHour)}</dd>
          </div>
          <div>
            <dt>Est. time to defeat</dt>
            <dd>
              {data.rate.estimatedHoursToDefeat == null
                ? '—'
                : `${data.rate.estimatedHoursToDefeat.toFixed(1)}h`}
            </dd>
          </div>
          <div>
            <dt>Attackers</dt>
            <dd>{formatNumber(data.totals.attackers)}</dd>
          </div>
          <div>
            <dt>Attempts spent</dt>
            <dd>{formatNumber(data.totals.attempts)}</dd>
          </div>
          <div>
            <dt>Faint rate</dt>
            <dd>{Math.round(data.rate.faintRate * 100)}%</dd>
          </div>
        </dl>

        {data.boss.hpAdjustedAt && (
          <p className="wb-admin__warning">
            The HP target was changed on {formatDate(data.boss.hpAdjustedAt)}. Players can see this on
            the fight's page.
          </p>
        )}
      </Panel>

      <Panel title="Damage distribution">
        <div className="wb-admin__hist">
          {data.histogram.map((bucket) => (
            <div key={bucket.from} className="wb-admin__hist-row">
              <span className="mono tiny">
                {formatNumber(bucket.from)}
                {bucket.to == null ? '+' : `–${formatNumber(bucket.to)}`}
              </span>
              <span className="wb-admin__hist-bar">
                <span
                  className="wb-admin__hist-fill"
                  style={{ width: `${(bucket.count / maxBucket) * 100}%` }}
                />
              </span>
              <span className="mono tiny">{bucket.count}</span>
            </div>
          ))}
        </div>
      </Panel>

      {can('events.publish') && (
        <Panel title="Controls" hint="Changes what the fight will do — never what it has done">
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <Button
              size="sm"
              onClick={() =>
                act('/attacks', { paused: !data.boss.attacksPaused }, data.boss.attacksPaused ? 'Attacks resumed.' : 'Attacks paused.')
              }
            >
              {data.boss.attacksPaused ? 'Resume attacks' : 'Pause attacks'}
            </Button>

            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const reason = window.prompt('Why is this fight ending early? This is audited.');
                if (!reason || reason.trim().length < 5) return;
                void act('/end', { reason: reason.trim() }, 'The fight is finalising.');
              }}
              disabled={data.boss.status !== 'LIVE'}
            >
              End now
            </Button>

            {can('events.grant') && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void act('/finalize', {}, 'Finalisation run.')}
                  disabled={data.boss.status !== 'FINALIZING'}
                >
                  Finalise now
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void act('/scan', {}, 'Integrity scan re-run.')}>
                  Re-run integrity scan
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const reason = window.prompt('Why are extra attempts being granted?');
                    if (!reason || reason.trim().length < 3) return;
                    void act('/attempts', { amount: 1, reason: reason.trim() }, 'Everybody got an extra attempt.');
                  }}
                >
                  +1 attempt for everyone
                </Button>
              </>
            )}
          </div>

          <p className="tiny muted">
            "End now" does not decide the outcome — it settles the fight with whatever the numbers
            already say. There is deliberately no control that edits a trainer's damage or reorders
            a placing.
          </p>
        </Panel>
      )}

      {/* PART 26 and PART 46. A flag is information for a human, never an
          automatic penalty, so both decisions are here and both are audited. */}
      <Panel title="Needs a look" hint={`${data.review.length} placings`}>
        {data.review.length === 0 ? (
          <p className="tiny muted">Nothing flagged, and nothing waiting on a claim.</p>
        ) : (
          <div className="stack-sm">
            {data.review.map((row) => (
              <div key={row.userId} className="wb-admin__flag">
                <strong>
                  {row.rank != null ? `#${row.rank} · ` : ''}
                  {row.username}
                </strong>
                <span className="tiny muted">
                  {formatNumber(row.damage)} damage from {row.attempts} attempts ·{' '}
                  {row.accountStatus} · account is {row.accountAgeDays} days old ·{' '}
                  {row.rewardStatus}
                  {row.rewardDeadline && ` until ${formatDate(row.rewardDeadline)}`}
                </span>
                {row.flagReason && <span className="tiny">{row.flagReason}</span>}

                {can('events.grant') && row.rewardStatus === 'HELD' && (
                  <div className="row" style={{ gap: 6 }}>
                    <Button
                      size="sm"
                      onClick={() => {
                        const reason = window.prompt('Why is this placing being released?');
                        if (!reason || reason.trim().length < 5) return;
                        void act(
                          '/review',
                          { userId: row.userId, decision: 'RELEASE', reason: reason.trim() },
                          'Reward released.',
                        );
                      }}
                    >
                      Release reward
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const reason = window.prompt('Why is this placing being forfeited?');
                        if (!reason || reason.trim().length < 5) return;
                        void act(
                          '/review',
                          { userId: row.userId, decision: 'FORFEIT', reason: reason.trim() },
                          'Reward forfeited.',
                        );
                      }}
                    >
                      Forfeit
                    </Button>
                  </div>
                )}

                {can('events.grant') && row.rewardStatus !== 'HELD' && row.rewardStatus !== 'GRANTED' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const reason = window.prompt(
                        'Why is this reward being granted by hand? This is audited, and it cannot create a second copy.',
                      );
                      if (!reason || reason.trim().length < 5) return;
                      void act('/grant', { userId: row.userId, reason: reason.trim() }, 'Grant attempted.');
                    }}
                  >
                    Grant the missing reward
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Supply — design PART 48
   ------------------------------------------------------------------------- */

function Supply() {
  const state = useAsync<WorldBossSupply>(() => api.get('/admin/world-boss/supply'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 200 }} />;

  const species = state.data.species;

  return (
    <Panel title="Legendary supply" hint="Every World Boss Pokémon ever created">
      {species.length === 0 ? (
        <EmptyState art="◇" title="None yet">
          No World Boss has produced a Legendary. This is where the whole economy of them will live.
        </EmptyState>
      ) : (
        <div className="stack-sm">
          {species.map((entry) => (
            <div key={entry.speciesId} className="wb-admin__row">
              <img className="pixel" src={`/sprites/pokemon/${entry.speciesId}.png`} alt="" />
              <div className="wb-admin__row-body">
                <strong>{entry.name}</strong>
                <span className="tiny muted">
                  {entry.everCreated} ever created · {entry.stillHeld} still held ·{' '}
                  {entry.traded} traded on · {entry.listed} listed · {entry.shiny} shiny
                  {entry.gone > 0 && ` · ${entry.gone} no longer exist`}
                </span>
                <ul className="tiny muted" style={{ margin: 0, paddingLeft: '1.2em' }}>
                  {entry.events.map((event) => (
                    <li key={event.key}>
                      {event.name} ({formatDate(event.at)}) — {event.count}:{' '}
                      {event.winners.map((winner) => `#${winner.rank} ${winner.name}`).join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
